<template>
    <section v-if="dismissed" class="mt-0.5 flex items-start gap-3 rounded-lgr border border-valence-grey-250 bg-white p-4">
        <i class="text-lg/none mt-0.5 bi bi-lightbulb text-valence-primary-600" aria-hidden="true"></i>
        <p class="body text-valence-grey-700">
            You're all caught up. Your My Work insights are always available on the
            <BaseLink :href="MY_WORK_URL" class="body-semibold text-valence-primary-600 hover:underline">My Work page</BaseLink>.
        </p>
    </section>

    <section v-else-if="cards.length">
        <div class="mt-0.5 flex items-start justify-between gap-3">
            <h3 class="scaffold-body text-valence-grey-850">
                <i class="text-lg/none mr-1 bi bi-lightbulb" aria-hidden="true"></i>
                Some food for thought
            </h3>
            <span class="inline-flex shrink-0 flex-col items-end gap-0.5 details text-ink-muted sm:flex-row sm:items-center sm:gap-2">
                <span class="whitespace-nowrap">{{ index + 1 }} of {{ cards.length }}</span>
                <span class="hidden sm:inline" aria-hidden="true">·</span>
                <BaseButton
                    theme="tertiary"
                    size="sm"
                    class="details relative inline-flex items-center justify-center gap-1.5 p-0! whitespace-nowrap text-valence-primary-600 hover:underline"
                    :disabled="isSeeingFullInsight"
                    :aria-busy="isSeeingFullInsight"
                    @click="handleSeeAll">
                    <LoadingSpinner v-if="isSeeingFullInsight" class="absolute h-3.5 w-3.5" />
                    <span :class="{ invisible: isSeeingFullInsight }">See full insight</span>
                </BaseButton>
            </span>
        </div>

        <HomeMyWorkInsightCard
            class="mt-4"
            :insight-id="current.insightId"
            :category="current.category"
            :experiment-slug="current.experimentSlug"
            :title="current.title"
            :summary="current.summary"
            :created-at="current.createdAt"
            :show-close="index === cards.length - 1"
            :perspective-enabled="perspectiveEnabled"
            :collaboration-profile-enabled="collaborationProfileEnabled"
            :work-iq-enabled="workIqEnabled"
            :calendar-enabled="calendarIntegrationEnabled"
            :perspective-present="perspectivePresent"
            :collaboration-profile-present="collaborationProfilePresent"
            :work-iq-present="workIqPresent"
            :evidence-groups="current.evidenceGroups"
            :is-starting-chat="isDivingDeeper"
            :chat-error="chatError"
            @close="handleClose"
            @dive-deeper="handleDiveDeeper" />

        <div v-if="cards.length > 1" class="mt-4 flex items-center justify-center gap-3">
            <button
                type="button"
                class="flex size-8 items-center justify-center rounded-full border border-valence-grey-300 text-valence-grey-600 transition-colors hover:bg-valence-grey-50 focus-ring disabled:pointer-events-none disabled:opacity-40"
                :disabled="index === 0"
                aria-label="Previous insight"
                @click="move(-1)">
                <i class="bi bi-arrow-left-short text-xl/none" aria-hidden="true"></i>
            </button>
            <div class="flex items-center gap-1.5">
                <button
                    v-for="i in cards.length"
                    :key="i"
                    type="button"
                    class="h-1.5 rounded-full transition-[width]"
                    :class="i - 1 === index ? 'w-4.5 bg-valence-primary-500' : 'w-1.5 bg-valence-grey-300'"
                    :aria-label="`Go to insight ${i}`"
                    :aria-current="i - 1 === index"
                    @click="goTo(i - 1)"></button>
            </div>
            <button
                type="button"
                class="flex size-8 items-center justify-center rounded-full border border-valence-grey-300 text-valence-grey-600 transition-colors hover:bg-valence-grey-50 focus-ring disabled:pointer-events-none disabled:opacity-40"
                :disabled="index === cards.length - 1"
                aria-label="Next insight"
                @click="move(1)">
                <i class="bi bi-arrow-right-short text-xl/none" aria-hidden="true"></i>
            </button>
        </div>
    </section>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import HomeMyWorkInsightCard from "~vue/ChatHome/HomeMyWorkInsightCard.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import { useLabInsightChat } from "~vue/composables/useLabInsightChat";
import { useTimedShow } from "~vue/composables/useTimedShow";
import { useUIInteractions } from "~vue/composables/useUIInteractions";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { tierForExperimentSlug } from "~vue/Labs/insightCategory";
import BaseLink from "~vue/router/BaseLink.vue";
import type { HomepageMyWorkInsight } from "~vue/types/chat_home";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, ref, watch } from "vue";

const MY_WORK_URL = "/intelligence/my-work/";
const MAX_CARDS = 3;
const DISMISSED_BANNER_MS = 5000;

// Insights arrive as an Inertia prop (server-rendered by chat_home), not a client-side fetch.
const props = withDefaults(
    defineProps<{
        insights?: HomepageMyWorkInsight[];
        perspectiveEnabled?: boolean;
        collaborationProfileEnabled?: boolean;
        workIqEnabled?: boolean;
        calendarIntegrationEnabled?: boolean;
        perspectivePresent?: boolean;
        collaborationProfilePresent?: boolean;
        workIqPresent?: boolean;
    }>(),
    {
        insights: () => [],
        perspectiveEnabled: false,
        collaborationProfileEnabled: false,
        workIqEnabled: false,
        calendarIntegrationEnabled: true,
        perspectivePresent: false,
        collaborationProfilePresent: false,
        workIqPresent: false,
    },
);

const emit = defineEmits<{ retire: [] }>();

interface CarouselCard {
    insightId: string;
    category: string;
    experimentSlug: string;
    title: string;
    summary: string;
    createdAt: string | null;
    evidenceGroups: HomepageMyWorkInsight["evidence_groups"];
}

const cards = computed<CarouselCard[]>(() =>
    [...props.insights]
        .sort((a, b) => {
            const tierDiff = tierForExperimentSlug(b.experiment_slug) - tierForExperimentSlug(a.experiment_slug);
            if (tierDiff !== 0) {
                return tierDiff;
            }
            return a.title.localeCompare(b.title, undefined, { sensitivity: "base" });
        })
        .slice(0, MAX_CARDS)
        .map((insight) => ({
            insightId: insight.insight_id,
            category: insight.experiment_title,
            experimentSlug: insight.experiment_slug,
            title: insight.title,
            summary: insight.summary,
            createdAt: insight.created_at,
            evidenceGroups: insight.evidence_groups,
        })),
);

const index = ref(0);
const current = computed(() => cards.value[index.value]);

const { createInteraction } = useUIInteractions();
const { chatError, handleStartChat } = useLabInsightChat(() => current.value.insightId);
const isDivingDeeper = ref(false);

const dismissed = ref(false);
const isSeeingFullInsight = ref(false);
const { isShowing: bannerShowing, show: showDismissedBanner } = useTimedShow({ duration: DISMISSED_BANNER_MS });
watch(bannerShowing, (now, previous) => {
    if (previous && !now) {
        emit("retire");
    }
});

watch(
    cards,
    (value, previous) => {
        if (index.value > value.length - 1) {
            index.value = 0;
        }
        if (value.length && !previous?.length) {
            logUserInteraction(AmplitudeEventNames.MYWORK_HOMEPAGE_CAROUSEL_SHOWN, { insight_count: value.length });
        }
    },
    { immediate: true },
);

function move(delta: number) {
    const count = cards.value.length;
    if (!count) {
        return;
    }
    index.value = (index.value + delta + count) % count;
    logUserInteraction(AmplitudeEventNames.MYWORK_HOMEPAGE_CAROUSEL_NAVIGATED, { to_index: index.value });
}

function goTo(target: number) {
    if (target < 0 || target >= cards.value.length || target === index.value) {
        return;
    }
    index.value = target;
    logUserInteraction(AmplitudeEventNames.MYWORK_HOMEPAGE_CAROUSEL_NAVIGATED, { to_index: index.value });
}

// The last-slide X, "See full insight", and Dive Deeper all retire the batch.
async function recordBatchDismissal() {
    await createInteraction("mywork_insight_banner_dismissed");
}

function handleClose() {
    logUserInteraction(AmplitudeEventNames.MYWORK_HOMEPAGE_CAROUSEL_DISMISSED, { insight_count: cards.value.length });
    void recordBatchDismissal();
    dismissed.value = true;
    showDismissedBanner();
}

async function handleSeeAll() {
    if (isSeeingFullInsight.value) {
        return;
    }
    isSeeingFullInsight.value = true;
    logUserInteraction(AmplitudeEventNames.MYWORK_HOMEPAGE_CAROUSEL_SEE_ALL_CLICKED, { from_index: index.value });
    try {
        await recordBatchDismissal();
    } finally {
        router.visit(MY_WORK_URL);
    }
}

async function handleDiveDeeper() {
    if (isDivingDeeper.value) {
        return;
    }
    isDivingDeeper.value = true;
    const { insightId, experimentSlug } = current.value;
    logUserInteraction(AmplitudeEventNames.MYWORK_INSIGHT_CARD_CLICKED, {
        insight_id: insightId,
        experiment_slug: experimentSlug,
        location: "mywork_homepage_carousel",
    });
    logUserInteraction(AmplitudeEventNames.INSIGHT_DIVE_DEEPER_CLICKED, { insightId, location: "mywork_homepage_carousel" });
    try {
        await recordBatchDismissal();
        await handleStartChat();
    } finally {
        isDivingDeeper.value = false;
    }
}
</script>
